import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ChartNoAxesColumn, TriangleAlert } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SettingsGroup from '@components/settings/SettingsGroup';
import SettingRow from '@components/settings/SettingRow';
import SettingsLinkRow from '@components/settings/SettingsLinkRow';
import ProfileTab from '@components/settings/ProfileTab';
import SecurityTab from '@components/settings/SecurityTab';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectTheme, setTheme } from '@features/ui/uiSlice';
import { selectUser } from '@features/auth/authSlice';
import { SETTINGS_TABS } from '@constants/settings';

/** Persisted directly rather than through the store; nothing else reads them. */
const useLocalToggle = (key: string, fallback: boolean) => {
  const [value, setValue] = useState(() => {
    const saved = localStorage.getItem(key);
    return saved !== null ? saved === 'true' : fallback;
  });

  return [
    value,
    (next: boolean) => {
      setValue(next);
      localStorage.setItem(key, String(next));
    },
  ] as const;
};

const UserSettingsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentTheme = useAppSelector(selectTheme);
  const user = useAppSelector(selectUser);

  const [notificationsEnabled, setNotificationsEnabled] = useLocalToggle('notificationsEnabled', true);
  const [emailNotifications, setEmailNotifications] = useLocalToggle('emailNotifications', false);

  // A JWT may carry a singular `role`, a `roles` array, or both. Mirrors the
  // check inside AuditDashboardPage so the link never leads to a 403.
  const roles = useMemo(() => {
    const collected: string[] = [];
    if (user?.role) collected.push(user.role);
    if (Array.isArray(user?.roles)) {
      user.roles.forEach((role) => {
        if (!collected.includes(role)) collected.push(role);
      });
    }
    return collected;
  }, [user]);

  const canViewActivity = roles.includes('admin') || roles.includes('manager');
  const canViewAnomalies = roles.includes('admin');

  const tabs = SETTINGS_TABS.filter((tab) => tab.id !== 'administration' || canViewActivity);

  return (
    <div className="@container/settings flex max-w-3xl flex-col gap-6">
      <header>
        <h1 className="revy-cjk text-2xl font-semibold leading-[1.4] tracking-tight @lg/settings:text-3xl">
          {t('settings.title')}
        </h1>
        <p className="revy-cjk mt-1 text-sm leading-[1.6] text-muted-foreground">
          {t('settings.description')}
        </p>
      </header>

      <Tabs defaultValue={tabs[0].id}>
        {/* Underlined rather than the stock pill list: five labels in Japanese
            overflow a segmented control long before they overflow a rule. */}
        <TabsList className="h-auto w-fit max-w-full justify-start gap-1 overflow-x-auto rounded-none border-b bg-transparent p-0">
          {tabs.map(({ id, labelKey, icon: Icon }) => (
            <TabsTrigger
              key={id}
              value={id}
              className="gap-1.5 rounded-none border-b-2 border-transparent px-3 py-2 text-sm shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
            >
              <Icon className="size-4" strokeWidth={1.9} />
              <span className="revy-cjk whitespace-nowrap">{t(labelKey)}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile" className="mt-5">
          <ProfileTab />
        </TabsContent>

        <TabsContent value="security" className="mt-5">
          <SecurityTab />
        </TabsContent>

        <TabsContent value="notifications" className="mt-5">
          <SettingsGroup
            title={t('settings.notifications')}
            description={t('settings.notificationsDescription')}
            footnote={t('settings.notificationNote')}
          >
            <SettingRow
              htmlFor="setting-notifications"
              label={t('settings.enableNotifications')}
              control={
                <Switch
                  id="setting-notifications"
                  checked={notificationsEnabled}
                  onCheckedChange={setNotificationsEnabled}
                />
              }
            />
            <SettingRow
              htmlFor="setting-email-notifications"
              label={t('settings.emailNotifications')}
              control={
                <Switch
                  id="setting-email-notifications"
                  checked={emailNotifications}
                  disabled={!notificationsEnabled}
                  onCheckedChange={setEmailNotifications}
                />
              }
            />
          </SettingsGroup>
        </TabsContent>

        <TabsContent value="preferences" className="mt-5">
          <SettingsGroup title={t('settings.appearance')}>
            <SettingRow
              htmlFor="setting-theme"
              label={t('settings.theme')}
              control={
                <Select
                  value={currentTheme}
                  onValueChange={(value) => dispatch(setTheme(value as 'light' | 'dark'))}
                >
                  <SelectTrigger id="setting-theme">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="revy-ui">
                    <SelectItem value="light">{t('settings.themeLight')}</SelectItem>
                    <SelectItem value="dark">{t('settings.themeDark')}</SelectItem>
                  </SelectContent>
                </Select>
              }
            />
            <SettingRow
              htmlFor="setting-language"
              label={t('settings.selectLanguage')}
              control={
                <Select
                  value={i18n.language?.split('-')[0] || 'ja'}
                  onValueChange={(value) => i18n.changeLanguage(value)}
                >
                  <SelectTrigger id="setting-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="revy-ui">
                    <SelectItem value="ja">{t('settings.languageOptions.japanese')}</SelectItem>
                    <SelectItem value="en">{t('settings.languageOptions.english')}</SelectItem>
                  </SelectContent>
                </Select>
              }
            />
          </SettingsGroup>
        </TabsContent>

        {canViewActivity && (
          <TabsContent value="administration" className="mt-5">
            <SettingsGroup
              title={t('settings.activityTitle')}
              description={t('settings.activityDescription')}
            >
              <SettingsLinkRow
                icon={ChartNoAxesColumn}
                label={t('settings.openActivityDashboard')}
                onSelect={() => navigate('/admin/activity')}
              />
              {canViewAnomalies && (
                <SettingsLinkRow
                  icon={TriangleAlert}
                  tone="warning"
                  label={t('settings.openAnomalyTriage')}
                  onSelect={() => navigate('/admin/anomalies')}
                />
              )}
            </SettingsGroup>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default UserSettingsPage;
